import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent } from "react";
import { SuperHero } from "@bluebottlecoffee/design-system";
import sanityClient from "../../../lib/sanity-client";
import renderModule from "../../../lib/render-module";
import { toSuperHeroProps } from "../../../lib/transformers/super-hero";
import { LayoutWrapper } from "../../../components/LayoutWrapper";
import {
  getDefaultStaticPaths,
  getDefaultStaticProps,
} from "../../../lib/utils";
import { OurCoffee } from "../../../lib/sanity-schema";
import { BaseProps, filters } from "../../../lib/sanity/shared";

interface PageData extends BaseProps {
  ourCoffee: OurCoffee;
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } = await getDefaultStaticProps(context);

  const ourCoffee = await sanityClient().fetch<OurCoffee>(`*[
    ${filters.whereType("ourCoffee")} &&
    ${filters.excludeDrafts}
  ][0]`);

  return {
    props: {
      ourCoffee,
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = getDefaultStaticPaths;

function validate(data: OurCoffee) {
  if (!data.title) {
    throw Error("Coffee page must have a title.");
  }
}

const OurCoffeePage: FunctionComponent<PageData> = ({
  ourCoffee,
  ...layoutWrapperProps
}) => {
  validate(ourCoffee);
  const { region, lang } = layoutWrapperProps;

  return (
    <LayoutWrapper
      {...{
        ...layoutWrapperProps,
        pageTitle: ourCoffee.title,
        metaTags: ourCoffee.metaTags,
      }}
    >
      {ourCoffee.hero?.length > 0 && (
        <SuperHero {...toSuperHeroProps(ourCoffee.hero, region, lang)} />
      )}
      {ourCoffee.modules?.length > 0 &&
        ourCoffee.modules.map((m) => renderModule(m, region, lang))}
    </LayoutWrapper>
  );
};

export default OurCoffeePage;
